'use client';

import { useEffect, useState, useCallback } from 'react';
import {
	useBlockingRouter,
	UseBlockingRouterOptions,
} from './useBlockingRouter';
import { isNumeric } from '../lib/helpers/types';

export type UseTabsProps<T extends Record<string, number | string>> = {
	tabsId?: string;
	tabs: T;
	handleQueryParam?: boolean;
	resetOnRemount?: boolean;
	initialValue?: T[keyof T];
} & Pick<UseBlockingRouterOptions, 'bypassBlockers' | 'overrideBlockers'>;

export const useTabs = <T extends Record<string, number | string>>( {
	tabsId = 'tab',
	tabs,
	initialValue = tabs[ Object.keys( tabs )[ 0 ] as keyof T ],
	handleQueryParam = false,
	resetOnRemount = true,
	...blockerProps
}: UseTabsProps<T> ) => {
	const { push, events, query } = useBlockingRouter();

	const [ value, setValue ] = useState<T[keyof T]>();

	const getKeyForValue = useCallback(
		( value: T[keyof T] ): keyof T | undefined =>
			Object.keys( tabs ).find( ( key ) => tabs[ key as keyof T ] === value ),
		[ tabs ]
	);

	const isKeyOfTabs = useCallback(
		( key?: string | string[] | undefined | keyof T ): key is keyof T =>
			typeof key === 'string' && Object.keys( tabs ).includes( key ),
		[ tabs ]
	);

	const getValueForKey = useCallback(
		( key: keyof T ): T[keyof T] | undefined => tabs[ key ],
		[ tabs ]
	);

	// set the initial value
	useEffect( () => {
		// If handleQueryParam is true, try to read from query params first on mount
		if ( handleQueryParam && query[ tabsId ] ) {
			const queryValue = Array.isArray( query[ tabsId ] )
				? query[ tabsId ][ 0 ]
				: query[ tabsId ];
			if ( typeof queryValue === 'string' && isKeyOfTabs( queryValue ) ) {
				const tabValue = getValueForKey( queryValue );
				if ( typeof tabValue !== 'undefined' ) {
					setValue( tabValue );
					return;
				}
			}
		}
		setValue( initialValue );
	}, [
		initialValue,
		handleQueryParam,
		query,
		tabsId,
		isKeyOfTabs,
		getValueForKey,
	] );

	/* Using router events + beforeHistoryChange because query reads and
	routeChangeComplete are not reliable enough for this hook during shallow routing. */
	useEffect( () => {
		if ( handleQueryParam ) {
			const onRouteChange = ( url: string ) => {
				const params =
					url.indexOf( '?' ) !== -1 && url.indexOf( `${ tabsId }=` ) !== -1
						? url.split( '?' )[ 1 ]
						: undefined;
				if ( params ) {
					const queryParams = new URLSearchParams( params );
					const queryValue = queryParams.get( tabsId );
					const urlTab: T[keyof T] | undefined =
						queryValue && isKeyOfTabs( queryValue )
							? getValueForKey( queryValue )
							: undefined;
					if ( typeof urlTab !== 'undefined' ) {
						setValue( urlTab );
					}
				}
			};
			events.on( 'beforeHistoryChange', onRouteChange );
			return () => {
				events.off( 'beforeHistoryChange', onRouteChange );
			};
		}
	}, [
		events,
		getValueForKey,
		handleQueryParam,
		isKeyOfTabs,
		tabsId
	] );

	// These accessibility props in a11yProps and TabPanel reference each other
	const a11yProps = useCallback(
		( tabsId: string ) => ( tabId: T[keyof T] ) => ( {
			id: `${ tabsId }-${ tabId.toString().toLowerCase().replace( / /g, '-' ) }`,
			'aria-controls': `${ tabsId }-${ getKeyForValue( tabId )?.toString() }`,
		} ),
		[ getKeyForValue ]
	);

	const idAndAssistanceProps = a11yProps( tabsId );

	useEffect( () => {
		/* make sure that, if the value is no longer in the array of visible tabs, like during
		sign-out, we inform the parent to use the first one, as wwe are sure it exists */
		if ( resetOnRemount ) {
			setValue( ( prevState ) =>
				prevState === initialValue ? prevState : initialValue
			);
		}
	}, [ initialValue, resetOnRemount ] );

	const handleChange = useCallback(
		async ( e: React.SyntheticEvent, value: T[keyof T] ) => {
			const newValue =
				typeof value === 'string' && isNumeric( value )
					? ( parseInt( value ) as T[keyof T] )
					: value;
			if ( handleQueryParam ) {
				await push(
					{
						query: { ...query, [ tabsId ]: tabs[ newValue ]?.toString() },
					},
					{
						...blockerProps,
					}
				);
			} else {
				setValue( ( prevState ) =>
					prevState === newValue ? prevState : newValue
				);
			}
		},
		[
			handleQueryParam,
			push,
			blockerProps,
			query,
			tabs,
			tabsId
		]
	);

	return {
		value: typeof value !== 'undefined' ? value : initialValue,
		handleChange,
		idAndAssistanceProps,
		reset: () => setValue( initialValue ),
	};
};
